import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A team (fields read defensively via the accessors below). */
export interface Team {
  id: number;
  name?: string | null;
  description?: string | null;
  status?: string | null;
  organization?: number | null;
  /** Member user ids (the GET response uses `member_ids`). */
  member_ids?: number[] | null;
  /** Member user objects (the GET response uses `members`). */
  members?: Array<{ id?: number; full_name?: string; name?: string; email?: string }> | null;
  /** Legacy/alternate shape. */
  users?: number[] | Array<{ id?: number; full_name?: string; name?: string } | number> | null;
  user_count?: number | null;
  members_count?: number | null;
}

/** Request body for POST /access/team/. */
export interface CreateTeamPayload {
  name: string;
  description: string;
  users: number[];
  status?: string;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result: current page rows plus the server's total count. */
export interface Paginated<T> {
  items: T[];
  total: number;
}
const toPaginated = <T>(r: Listish<T>): Paginated<T> => {
  const items = asList(r);
  const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
  return { items, total };
};

/* ------------------------------------------------------------------ *
 * Defensive accessors.
 * ------------------------------------------------------------------ */
export const teamName = (t: Team) => t.name || 'Untitled team';
export const teamDesc = (t: Team) => t.description || '';
/** The member user ids — from `member_ids`, or `members[].id`, or a legacy `users` array. */
export const teamUserIds = (t: Team): number[] => {
  if (Array.isArray(t.member_ids)) {
    return t.member_ids.filter((v): v is number => typeof v === 'number');
  }
  if (Array.isArray(t.members)) {
    return t.members.map((m) => m?.id).filter((v): v is number => typeof v === 'number');
  }
  if (Array.isArray(t.users)) {
    return t.users
      .map((u) => (typeof u === 'number' ? u : u?.id))
      .filter((v): v is number => typeof v === 'number');
  }
  return [];
};
export const teamMemberCount = (t: Team): number =>
  t.user_count ?? t.members_count ?? teamUserIds(t).length;
/** Member display names, when the API returns user objects (empty for bare-id responses). */
export const teamMemberNames = (t: Team): string[] => {
  if (Array.isArray(t.members)) {
    return t.members.map((m) => m?.full_name || m?.name || '').filter((v): v is string => Boolean(v));
  }
  if (Array.isArray(t.users)) {
    return t.users
      .map((u) => (typeof u === 'number' ? '' : u?.full_name || u?.name || ''))
      .filter((v): v is string => Boolean(v));
  }
  return [];
};

export const teamsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTeams: build.query<
      Paginated<Team>,
      { search?: string; isActive?: boolean | ''; page?: number; pageSize?: number } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.team}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.isActive === true || arg?.isActive === false ? { is_active: arg.isActive } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<Team>) => toPaginated(r),
      providesTags: ['Teams'],
    }),
    getTeam: build.query<Team, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.team}/${id}/` }),
      providesTags: ['Teams'],
    }),
    createTeam: build.mutation<Team, CreateTeamPayload>({
      query: (body) => ({ url: `${ApiEndPoints.team}/`, method: 'POST', body }),
      invalidatesTags: ['Teams'],
    }),
    updateTeam: build.mutation<Team, { id: number | string; patch: Partial<CreateTeamPayload> }>({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.team}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Teams'],
    }),
    deleteTeam: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.team}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Teams'],
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
} = teamsApi;
